// Shared by Cloud Functions and the frontend (imported there as @shared).
// The strategy review on a game page: how each side's choices worked,
// read from the game's box score next to both teams' season numbers
// (summary/teamStats, which the page already has). Public information
// only: it never uses the server-only counter table or hidden system fit,
// so it says what happened, not why the sim made it happen.
//
// One game is noisy. The grade takes out the one thing no strategy
// touches (free throw luck) and the page says the rest plainly.

import {
  DEFENSES, DEPTHS, OFFENSES, PACES, strategyLabel,
} from "./strategy.js";
import {TEAM_STAT_KEYS, possessions} from "./teamStats.js";

// Season games (not counting this one) a team needs before its averages
// mean anything.
export const REVIEW_MIN_GAMES = 3;
// Points per 100 possessions above or below the expected that count as a
// choice working (or not).
const CLEAR = 5;
// Plan grade cutoffs: points per 100 better than expected. One college
// game spreads about 21 per 100 around the expected (test/gameReview),
// so about one game in eight earns an A and one in eight an F.
const GRADES = [[20, "A"], [8, "B"], [-8, "C"], [-20, "D"]];

const pct = (m, a) => (a ? m / a : 0);
const pctText = (m, a) => `${Math.round(100 * pct(m, a))}%`;
const name = (s) => s.abbr ?? s.name;
const per100 = (pts, poss) => (poss ? 100 * pts / poss : 0);

/**
 * A team's season totals without this game (when they include it).
 * @param {object|undefined} stats season totals (shared/teamStats.js)
 * @param {object} own this game's totals for the team
 * @param {object} opp the opponent's game totals
 * @param {boolean} includes the season totals count this game
 * @return {object|null} null with too few games
 */
export function seasonWithout(stats, own, opp, includes) {
  if (!stats) return null;
  const out = {gp: stats.gp - (includes ? 1 : 0), opp: {}};
  for (const k of TEAM_STAT_KEYS) {
    out[k] = (stats[k] ?? 0) - (includes ? own?.[k] ?? 0 : 0);
    out.opp[k] = (stats.opp?.[k] ?? 0) - (includes ? opp?.[k] ?? 0 : 0);
  }
  return out.gp >= REVIEW_MIN_GAMES ? out : null;
}

/**
 * The league's points per 100 possessions this season.
 * @param {object} statsByTeam {teamId: stats}
 * @return {number|null}
 */
function leagueRating(statsByTeam) {
  let pts = 0;
  let poss = 0;
  for (const s of Object.values(statsByTeam ?? {})) {
    pts += s.pts ?? 0;
    poss += possessions(s);
  }
  return poss ? per100(pts, poss) : null;
}

/**
 * @param {object} side game side
 * @param {function} test (player) => boolean on the roster entry
 * @return {object[]} box lines with names, most minutes first
 */
function linesOf(side, test = () => true) {
  return Object.entries(side.players ?? {})
      .map(([id, b]) => ({id, ...b, ...side.roster?.[id]}))
      .filter((p) => test(p))
      .sort((a, b) => (b.min ?? 0) - (a.min ?? 0));
}

/**
 * @param {object} p box line with names
 * @return {string} "J. Smith"
 */
function short(p) {
  return p.lName ? `${p.fName ? `${p.fName[0]}. ` : ""}${p.lName}` :
    "a player";
}

/**
 * @param {number|string} pos pos1 (1-5, or "PG".."C" in older data)
 * @return {number}
 */
function posNumber(pos) {
  const n = Number(pos);
  if (n >= 1 && n <= 5) return n;
  return ["PG", "SG", "SF", "PF", "C"].indexOf(pos) + 1;
}

/**
 * A verdict from points per 100 against the expected.
 * @param {number|null} diff
 * @return {string} "good" | "poor" | "even" | "info"
 */
function verdictOf(diff) {
  if (diff === null) return "info";
  return diff >= CLEAR ? "good" : diff <= -CLEAR ? "poor" : "even";
}

/**
 * What the offense's choice was about, from the box score.
 * @param {string} offense
 * @param {object} us game side
 * @param {object|null} season our season (without this game)
 * @return {string}
 */
function offenseDetail(offense, us, season) {
  const t = us.totals;
  if (offense === "fiveOut") {
    return `${t.tpm} of ${t.tpa} from three, ${pctText(t.tpm, t.tpa)}` +
      (season ? ` (season ${pctText(season.tpm, season.tpa)}, ` +
        `${(season.tpa / season.gp).toFixed(1)} tries a game)` : "");
  }
  if (offense === "motion" || offense === "princeton") {
    const ratio = season ? `; season ${Math.round(100 *
      pct(season.ast, season.fgm))}% assisted` : "";
    return `${t.ast} assists on ${t.fgm} baskets, ${t.tov} turnovers` +
      `${ratio}`;
  }
  if (offense === "post") {
    const bigs = linesOf(us, (p) => posNumber(p.pos1) >= 4);
    const pts = bigs.reduce((s, p) => s + (p.pts ?? 0), 0);
    return `Forwards and centers scored ${pts}; ${t.fta} free throw ` +
      `attempts, ${t.oreb} offensive rebounds`;
  }
  if (offense === "pickRoll") {
    const pg = linesOf(us, (p) => posNumber(p.pos1) === 1)[0];
    return pg ? `${short(pg)}: ${pg.pts} points, ${pg.ast} assists, ` +
      `${pg.tov} turnovers` : `${t.ast} assists`;
  }
  if (offense === "isolation") {
    const top = [...linesOf(us)].sort((a, b) => b.pts - a.pts)[0];
    return top ? `${short(top)}: ${top.pts} points on ${top.fgm} of ` +
      `${top.fga}; team ${t.ast} assists` : `${t.ast} assists`;
  }
  return `${t.fgm} of ${t.fga} shooting (${pctText(t.fgm, t.fga)})`;
}

/**
 * What the defense's choice was about, from the other side's box score.
 * @param {string} defense
 * @param {object} us game side (the defense)
 * @param {object} them game side (the offense it faced)
 * @param {object|null} theirSeason their season (without this game)
 * @return {string}
 */
function defenseDetail(defense, us, them, theirSeason) {
  const t = them.totals;
  const s = theirSeason;
  const was = (m, a) => (s ? ` (season ${pctText(m, a)})` : "");
  const who = name(them);
  if (defense === "zone" || defense === "packLine") {
    const two = `${pctText(t.fgm - t.tpm, t.fga - t.tpa)} inside the arc` +
      (s ? ` (season ${pctText(s.fgm - s.tpm, s.fga - s.tpa)})` : "");
    const extra = defense === "zone" ? `, ${t.oreb} offensive rebounds` :
      "";
    return `${who}: ${t.tpm} of ${t.tpa} from three` +
      `${was(s?.tpm, s?.tpa)}, ${two}${extra}`;
  }
  if (defense === "press") {
    const avg = s ? ` (season ${(s.tov / s.gp).toFixed(1)})` : "";
    return `${who}: ${t.tov} turnovers${avg}; you had ${us.totals.stl} ` +
      `steals and ${us.totals.pf} fouls`;
  }
  if (defense === "switch") {
    const bigs = linesOf(them, (p) => posNumber(p.pos1) >= 4);
    const pts = bigs.reduce((n, p) => n + (p.pts ?? 0), 0);
    return `${who}: ${t.fgm} of ${t.fga}, ${pctText(t.fgm, t.fga)}` +
      `${was(s?.fgm, s?.fga)}; their forwards and centers scored ${pts}`;
  }
  return `${who}: ${t.fgm} of ${t.fga}, ${pctText(t.fgm, t.fga)}` +
    `${was(s?.fgm, s?.fga)}; ${t.tpm} of ${t.tpa} from three`;
}

/**
 * How keying on a player went.
 * @param {object} us the side that keyed
 * @param {object} them the side with the keyed player
 * @param {object|null} theirSeason
 * @return {object|null} item
 */
function keyOnItem(us, them, theirSeason) {
  const id = us.strategy?.keyOn;
  if (!id) return null;
  const p = them.players?.[id];
  const who = them.roster?.[id];
  const label = who ? `${who.fName ?? ""} ${who.lName ?? ""}`.trim() :
    "their player";
  if (!p || !p.min) {
    return {key: "keyOn", label: "Key on", choice: label, verdict: "info",
      text: `${label} didn't play.`};
  }
  const t = them.totals;
  const restM = t.fgm - p.fgm;
  const restA = t.fga - p.fga;
  const share = pct(p.fga, t.fga);
  const restPct = pct(restM, restA);
  const seasonPct = theirSeason ?
    pct(theirSeason.fgm, theirSeason.fga) : null;
  // Did it slow them down, and did the other four make them pay?
  const slowed = share <= 0.2 || pct(p.fgm, p.fga) < restPct - 0.05;
  const burned = seasonPct !== null && restPct >= seasonPct + 0.05;
  const verdict = slowed && !burned ? "good" :
    !slowed && p.pts >= 20 ? "poor" : burned ? "poor" : "even";
  return {
    key: "keyOn", label: "Key on", choice: label, verdict,
    text: `${label}: ${p.pts} points on ${p.fgm} of ${p.fga}, ` +
      `${Math.round(100 * share)}% of ${name(them)}'s shots. ` +
      `The rest shot ${restM} of ${restA} (${pctText(restM, restA)})` +
      (seasonPct !== null ?
        `; ${name(them)} shoots ${Math.round(100 * seasonPct)}% ` +
        `for the season.` : "."),
  };
}

/**
 * The pace choice next to the expected result: slowing down helps an
 * underdog (fewer possessions, fewer chances for talent to tell), speeding
 * up helps a favorite.
 * @param {string} pace
 * @param {number|null} expectedNet our expected points per 100 margin
 * @param {number} poss possessions this game
 * @param {number|null} usual the two teams' usual possessions
 * @return {object} item
 */
function paceItem(pace, expectedNet, poss, usual) {
  const count = `${Math.round(poss)} possessions` +
    (usual ? ` (these teams usually play about ${Math.round(usual)})` : "");
  let verdict = "info";
  let why = "";
  if (expectedNet !== null && pace !== "normal") {
    const underdog = expectedNet < -3;
    const favorite = expectedNet > 3;
    if ((underdog && pace === "slow") || (favorite && pace === "fast")) {
      verdict = "good";
      why = underdog ? " Slowing it down suits an underdog: fewer " +
        "possessions, fewer chances for talent to show." :
        " Speeding up suits a favorite: more possessions for the " +
        "better team.";
    } else if (underdog || favorite) {
      verdict = "poor";
      why = underdog ? " A fast game gives the better team more chances " +
        "to pull away." : " A slow game gives an underdog fewer " +
        "possessions to make up and a better chance to hang around.";
    }
  }
  return {key: "pace", label: "Pace", choice: strategyLabel(PACES, pace),
    verdict, text: `${count}.${why}`};
}

/**
 * The rotation: who played the minutes.
 * @param {string} depth
 * @param {object} us
 * @param {number} minutesPerGame
 * @return {object} item
 */
function depthItem(depth, us, minutesPerGame) {
  const lines = linesOf(us);
  const total = lines.reduce((s, p) => s + (p.min ?? 0), 0);
  const top7 = lines.slice(0, 7).reduce((s, p) => s + (p.min ?? 0), 0);
  const used = lines.filter((p) => (p.min ?? 0) >= 1).length;
  const bench = lines.filter((p) => !p.starter)
      .reduce((s, p) => s + (p.pts ?? 0), 0);
  const heavy = lines.filter((p) => p.min >= minutesPerGame * 0.9);
  const tired = heavy.length && depth === "tight" ?
    ` ${heavy.map(short).join(", ")} played ${
      heavy.length === 1 ? "almost the whole game" : "almost all of it"}.` :
    "";
  return {
    key: "depth", label: "Rotation", choice: strategyLabel(DEPTHS, depth),
    verdict: "info",
    text: `${used} players got in; the top seven played ` +
      `${pctText(top7, total)} of the minutes. Bench points: ${bench}.` +
      tired,
  };
}

/**
 * Free throw points above (or below) the team's season percentage: luck
 * no strategy controls.
 * @param {object} t game totals
 * @param {object|null} season
 * @return {number}
 */
function freeThrowLuck(t, season) {
  if (!season || !season.fta || !t.fta) return 0;
  return t.ftm - t.fta * pct(season.ftm, season.fta);
}

/**
 * @param {number} score points per 100 better than expected
 * @return {string} A-F
 */
export function planGrade(score) {
  return GRADES.find(([cut]) => score >= cut)?.[1] ?? "F";
}

/**
 * The strategy review of a game, one entry per side that has a strategy.
 * @param {object} game game document
 * @param {object} [ctx]
 * @param {object} [ctx.teamStats] summary/teamStats {teams, playoffs}
 * @param {number} [ctx.seasonYear] the league's current season (season
 *   numbers only apply to its games)
 * @param {string} [ctx.type] league type
 * @param {number} [ctx.minutesPerGame]
 * @return {object|null} {sides: [review], luck: {text, swing, teamId}|null,
 *   compared: whether season numbers were used}
 */
export function reviewGame(game, ctx = {}) {
  const home = game?.home;
  const away = game?.away;
  if (!home?.totals || !away?.totals) return null;
  if (!home.strategy && !away.strategy) return null;
  const sameSeason = ctx.seasonYear === undefined ||
    (game.year ?? 1) === ctx.seasonYear;
  // Pro playoff games are kept apart from the season totals; compare them
  // with the regular season.
  const proPlayoff = ctx.type === "pro" && Boolean(game.post);
  const all = sameSeason ? ctx.teamStats?.teams : null;
  const seasonOf = (s, o) => seasonWithout(all?.[s.id], s.totals, o.totals,
      !proPlayoff);
  const seasons = {[home.id]: seasonOf(home, away),
    [away.id]: seasonOf(away, home)};
  const league = all ? leagueRating(all) : null;
  const poss = (possessions(home.totals) + possessions(away.totals)) / 2;
  const minutes = ctx.minutesPerGame ?? (ctx.type === "pro" ? 48 : 40);

  const offRtg = (s) => per100(s.pts, possessions(s));
  const defRtg = (s) => per100(s.opp.pts, possessions(s.opp));
  // What the season numbers say a team scores per 100 against this
  // defense.
  const expectedOff = (us, them) => {
    const a = seasons[us.id];
    const b = seasons[them.id];
    return a && b && league !== null ? offRtg(a) + defRtg(b) - league : null;
  };
  const luck = Object.fromEntries([home, away].map((s) =>
    [s.id, freeThrowLuck(s.totals, seasons[s.id])]));

  const review = (us, them) => {
    const s = us.strategy;
    const expOff = expectedOff(us, them);
    const expDef = expectedOff(them, us);
    const actOff = per100(us.score, poss);
    const actDef = per100(them.score, poss);
    const offDiff = expOff === null ? null : actOff - expOff;
    const defDiff = expDef === null ? null : expDef - actDef;
    const expectedNet = expOff === null || expDef === null ? null :
      expOff - expDef;
    const per = (x) => `${Math.round(x)} per 100`;
    const by = (x, up, down) => `${Math.abs(x).toFixed(1)} ${
      x >= 0 ? up : down}`;
    const items = [
      {key: "offense", label: "Offense",
        choice: strategyLabel(OFFENSES, s.offense),
        verdict: verdictOf(offDiff),
        text: `${us.score} points, ${per(actOff)}` +
          (offDiff === null ? "" : ` (${by(offDiff, "above", "below")} ` +
            `the ${per(expOff)} expected against ${name(them)})`) +
          `. ${offenseDetail(s.offense, us, seasons[us.id])}.`},
      {key: "defense", label: "Defense",
        choice: strategyLabel(DEFENSES, s.defense),
        verdict: verdictOf(defDiff),
        text: `Allowed ${them.score}, ${per(actDef)}` +
          (defDiff === null ? "" : ` (${by(defDiff, "fewer", "more")} ` +
            `than the ${per(expDef)} expected)`) +
          `. ${defenseDetail(s.defense, us, them, seasons[them.id])}.`},
      keyOnItem(us, them, seasons[them.id]),
      paceItem(s.pace, expectedNet, poss, usualPace(seasons, us, them)),
      depthItem(s.depth, us, minutes),
    ].filter(Boolean);
    let grade = null;
    let score = null;
    if (offDiff !== null && defDiff !== null) {
      // Free throws are luck as far as strategy goes: take them out.
      score = offDiff + defDiff -
        per100(luck[us.id], poss) + per100(luck[them.id], poss);
      grade = planGrade(score);
    }
    return {id: us.id, abbr: us.abbr, name: us.name, grade,
      score: score === null ? null : Math.round(score * 10) / 10,
      expectedNet: expectedNet === null ? null :
        Math.round(expectedNet * 10) / 10,
      expectedMargin: expectedNet === null ? null :
        Math.round(expectedNet * poss / 10) / 10,
      items};
  };

  const sides = [away, home].filter((s) => s.strategy)
      .map((s) => review(s, s === home ? away : home));
  // The free throw swing, from the home side's view.
  const swing = luck[home.id] - luck[away.id];
  const ft = (s) => `${name(s)} ${s.totals.ftm} of ${s.totals.fta}` +
    (seasons[s.id] ? ` (${pctText(s.totals.ftm, s.totals.fta)}; season ` +
      `${pctText(seasons[s.id].ftm, seasons[s.id].fta)})` : "");
  const luckLine = seasons[home.id] && seasons[away.id] ? {
    teamId: swing >= 0 ? home.id : away.id,
    swing: Math.round(Math.abs(swing) * 10) / 10,
    text: `Free throws: ${ft(away)}, ${ft(home)}.`,
  } : null;
  return {sides, luck: luckLine,
    compared: Boolean(seasons[home.id] && seasons[away.id])};
}

/**
 * The possessions the two teams usually play, averaged.
 * @param {object} seasons {teamId: season}
 * @param {object} a
 * @param {object} b
 * @return {number|null}
 */
function usualPace(seasons, a, b) {
  const one = (s) => s ? (possessions(s) + possessions(s.opp)) / 2 / s.gp :
    null;
  const x = one(seasons[a.id]);
  const y = one(seasons[b.id]);
  return x !== null && y !== null ? (x + y) / 2 : null;
}
